import { defineStore } from "pinia";

import { i18n } from "../../i18n.js";
import { validateInput, computeCustomFormatLabel } from "../../helpers/helpers";
import { useChangeOptionsStore } from "@/stores/modules/changeOptions";
import { useWebproductStore } from "@/stores/modules/webproduct";

export const useCustomStore = defineStore("custom", {
  state: () => ({
    formats: {},
    quantities: []
  }),

  getters: {
    getCustomFormats(state) {
      const webproductStore = useWebproductStore();
      return state.formats[webproductStore.getActiveProductPartCode]
        ? state.formats[webproductStore.getActiveProductPartCode]
        : [];
    },
    getCustomQuantities(state) {
      return state.quantities;
    },
    getCustomQuantitiesLabels(state) {
      return this.getCustomQuantities.map(el => {
        return el.label;
      });
    },
    getInputQuantityMinMax(state) {
      const webproductStore = useWebproductStore();
      return {
        min: parseInt(webproductStore.getConfig.min_qty),
        max: parseInt(webproductStore.getConfig.max_qty)
      };
    },
    getLocalStorageName(state) {
      const webproductStore = useWebproductStore();
      return (
        webproductStore.getWebProductId +
        "_" +
        webproductStore.getWebproductHashData
      ); // + checksum or something from webproduct.data
    },
    getCustomFormatLimits(state) {
      const webproductStore = useWebproductStore();
      var activePPOptions = webproductStore.getActiveProductPageOptions;
      if (!activePPOptions.format) {
        return {};
      }

      var width_min = null;
      var width_max = null;
      var height_min = null;
      var height_max = null;

      var formats = [];

      for (var option of activePPOptions.format.values) {
        var width = parseFloat(option.attributes.width);
        var height = parseFloat(option.attributes.height);

        formats.push({
          width,
          height,
          name: option.value
        });

        if (width_min == null) {
          width_min = width;
          width_max = width;
          height_min = height;
          height_max = height;
        } else {
          if (width > width_max) {
            width_max = width;
          } else if (width < width_min) {
            width_min = width;
          }

          if (height > height_max) {
            height_max = height;
          } else if (height < height_min) {
            height_min = height;
          }
        }
      }

      // add also the current custom formats ... we need to compare with them also
      for (var format of this.getCustomFormats) {
        formats.push({
          width: format.width,
          height: format.height,
          name: format.value
        });
      }
      return {
        formats,
        limits: {
          width_min,
          width_max,
          height_min,
          height_max
        }
      };
    }
  },

  actions: {
    setCustomQuantity(payload) {
      this.quantities.push({
        label: "custom_qty_" + payload,
        qty: payload
      });
      const changeOptionsStore = useChangeOptionsStore();
      changeOptionsStore.setQuantity({
        qty: payload,
        qtyCode: "custom_qty_" + payload
      });
      this.setCustomFromLocalStorage();
    },
    setCustomFormat(payload) {
      const webproductStore = useWebproductStore();
      var formatsPP = { ...this.formats };
      if (!formatsPP[webproductStore.getActiveProductPartCode]) {
        formatsPP[webproductStore.getActiveProductPartCode] = [];
      }
      formatsPP[webproductStore.getActiveProductPartCode].push({
        label: computeCustomFormatLabel(
          webproductStore.getMeasurementSystem,
          payload.width,
          payload.height
        ),
        value: "custom_" + payload.width + "_" + payload.height,
        width: payload.width,
        height: payload.height
      });
      this.formats = formatsPP;
      const changeOptionsStore = useChangeOptionsStore();
      changeOptionsStore.setPageOption({
        optionType: "main",
        value: "custom_" + payload.width + "_" + payload.height,
        option: "format"
      });
      this.setCustomFromLocalStorage();
    },
    setCustomFromLocalStorage() {
      localStorage[this.getLocalStorageName] = JSON.stringify(this.$state);
    },
    loadCustomFromLocalStorage() {
      var localStorageKey = this.getLocalStorageName;
      if (localStorage[localStorageKey]) {
        var newState = JSON.parse(localStorage[localStorageKey]);
        this.formats = newState.formats;
        this.quantities = newState.quantities;
      }
    },
    inputCustomFormat({ width, height }) {
      const webproductStore = useWebproductStore();
      var formatLimits = this.getCustomFormatLimits;
      var widthValid = validateInput(
        width.toString(),
        i18n.t("Width"),
        formatLimits.limits.width_min,
        formatLimits.limits.width_max
      );
      if (!widthValid.valid) {
        webproductStore.getSweetAlert({
          text: widthValid.msg,
          icon: "warning"
        });
        return;
      }
      var heightValid = validateInput(
        height.toString(),
        i18n.t("Height"),
        formatLimits.limits.height_min,
        formatLimits.limits.height_max
      );
      if (!heightValid.valid) {
        webproductStore.getSweetAlert({
          text: heightValid.msg,
          icon: "warning"
        });
        return;
      }

      // check if this format already exists
      for (var format of formatLimits.formats) {
        if (
          format.width === widthValid.number &&
          format.height === heightValid.number
        ) {
          const changeOptionsStore = useChangeOptionsStore();
          changeOptionsStore.setPageOption({
            optionType: "main",
            value: format.name,
            option: "format"
          });
          return;
        }
      }
      this.setCustomFormat({
        width: widthValid.number,
        height: heightValid.number
      });
    }
  }
});
